// src/pages/LabelTemplateListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaStar,
  FaRegStar,
  FaQrcode,
  FaBarcode,
  FaSearch,
} from "react-icons/fa";
import { useSelector } from "react-redux";

import { useDebounce } from "../hooks/useDebounced";
import {
  useSetDefaultLabelTemplateMutation,
  useGetAllLabelTemplatesQuery,
  useDeleteLabelTemplateMutation,
} from "../toolkitServices/labelTemplateApi";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import ConfirmDialog from "../components/ConfirmDialog";

const LabelTemplateListPage = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 800);

  const authUser = useSelector((s) => s.auth?.user);
  const clientCode = authUser?.clientCode;

  /* 🔹 Templates list */
  const {
    data: templatesRes,
    isLoading,
    isError,
    error,
  } = useGetAllLabelTemplatesQuery(
    clientCode ? { clientCode } : {},
    { skip: false }
  );

  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteLabelTemplateMutation();
  const [setDefaultTemplate] = useSetDefaultLabelTemplateMutation();

  /* 🔹 Branch & Category master (for name resolve) */
  const { data: branchesRes } = useGetAllBranchesQuery(
    { page: 1, limit: 1000, active: true },
    { skip: false }
  );

  const { data: categoriesRes } = useGetAllCategoriesQuery(
    { page: 1, limit: 1000, active: true },
    { skip: false }
  );

  const templates = useMemo(() => {
    const list = templatesRes?.data || templatesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [templatesRes]);

  const branches = useMemo(() => {
    const list = branchesRes?.data || branchesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [branchesRes]);

  const categories = useMemo(() => {
    const list = categoriesRes?.data || categoriesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [categoriesRes]);

  // 🔹 Maps: id -> name
  const branchMap = useMemo(() => {
    const map = {};
    branches.forEach((b) => {
      map[b._id] =
        b.name || b.branchName || b.code || b.shortName || "Branch";
    });
    return map;
  }, [branches]);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[c._id] = c.name || c.categoryName || c.code || "Category";
    });
    return map;
  }, [categories]);

  // search filter (front-end)
  const filteredTemplates = useMemo(() => {
    if (!debouncedSearch) return templates;
    const term = debouncedSearch.toLowerCase();

    return templates.filter((tpl) => {
      const name = (tpl.name || "").toLowerCase();
      const size = `${tpl.widthMm || ""}x${tpl.heightMm || ""}`.toLowerCase();
      return name.includes(term) || size.includes(term);
    });
  }, [templates, debouncedSearch]);

  // Keyboard shortcut: Ctrl + L → New Template (agar koi template nahi hai)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "l" && !templates?.length) {
        e.preventDefault();
        navigate("/settings/label-templates/new");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, templates]);

  // 🔥 Confirm dialog state for delete
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    tpl: null,
  });

  const openDeleteDialog = (tpl) => {
    setDeleteDialog({ open: true, tpl });
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return; // deleting ke beech me cancel mat karo
    setDeleteDialog({ open: false, tpl: null });
  };

  const handleConfirmDelete = async () => {
    const tpl = deleteDialog.tpl;
    if (!tpl) return;

    try {
      await deleteTemplate(tpl._id).unwrap();
    } catch (err) {
      console.error("Delete template error:", err);
      // yaha toast bhi kar sakte ho
    } finally {
      setDeleteDialog({ open: false, tpl: null });
    }
  };

  const handleSetDefault = async (tpl) => {
    try {
      await setDefaultTemplate({ id: tpl._id, clientCode }).unwrap();
    } catch (err) {
      console.error("Set default template error:", err);
      // optionally toast
    }
  };

  // 🧩 Small helper: chip-style scope renderer
  const renderScopeChips = (names = [], allLabel) => {
    if (allLabel) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">
          {allLabel}
        </span>
      );
    }

    if (!names.length) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-dashed border-slate-200 text-[10px]">
          Not mapped
        </span>
      );
    }

    const shown = names.slice(0, 2);
    const remaining = names.length - shown.length;

    return (
      <div className="flex flex-wrap gap-1">
        {shown.map((n, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[10px]"
          >
            {n}
          </span>
        ))}
        {remaining > 0 && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-dashed border-slate-300 text-[10px]"
            title={names.join(", ")}
          >
            +{remaining} more
          </span>
        )}
      </div>
    );
  };

  const renderScope = (tpl) => {
    // Branch names
    let branchNames = [];
    let branchAllLabel = null;
    if (tpl.appliesToAllBranches) {
      branchAllLabel = "All branches";
    } else if (Array.isArray(tpl.branchIds) && tpl.branchIds.length) {
      branchNames = tpl.branchIds.map((id) => branchMap[id] || id);
    }

    // Category names
    let categoryNames = [];
    let categoryAllLabel = null;
    if (tpl.appliesToAllCategories) {
      categoryAllLabel = "All categories";
    } else if (Array.isArray(tpl.categoryIds) && tpl.categoryIds.length) {
      categoryNames = tpl.categoryIds.map((id) => categoryMap[id] || id);
    }

    return (
      <div className="flex flex-col gap-1 text-[11px] text-slate-700">
        {/* Branch row */}
        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 inline-flex h-5 px-1.5 rounded-full bg-violet-50 text-[9px] font-semibold text-violet-700 border border-violet-100 uppercase tracking-wide">
            Branch
          </span>
          {renderScopeChips(branchNames, branchAllLabel)}
        </div>

        {/* Category row */}
        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 inline-flex h-5 px-1.5 rounded-full bg-sky-50 text-[9px] font-semibold text-sky-700 border border-sky-100 uppercase tracking-wide">
            Category
          </span>
          {renderScopeChips(categoryNames, categoryAllLabel)}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-[#9481ce]  text-white">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Label Templates
            </h2>
            <p className="text-xs text-violet-100 mt-0.5">
              QR + barcode ready layouts – per branch & category mapping ke
              saath.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search box with icon */}
            <div className="relative">
              <span className="absolute inset-y-0 left-2 flex items-center text-[11px] text-slate-400">
                <FaSearch />
              </span>
              <input
                type="text"
                placeholder="Search by name or size..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7 pr-3 py-1.5 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-200 shadow-sm border border-violet-200/60"
              />
            </div>

            <Link
              to="/designs/label"
              className="bg-white text-[#5d4bb0] px-4 py-1.5 rounded-md text-sm font-medium shadow-sm hover:bg-violet-50 border border-violet-100"
            >
              + Add New
            </Link>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[calc(100vh-170px)] overflow-y-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-[#f1eefc] text-[#4b3d91] uppercase text-xs sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 font-semibold">Template Name</th>
                <th className="px-4 py-3 font-semibold">Size (mm)</th>
                <th className="px-4 py-3 font-semibold">Elements</th>
                <th className="px-4 py-3 font-semibold">Scope</th>
                <th className="px-4 py-3 font-semibold text-center">Default</th>
                <th className="px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white text-gray-700">
              <AnimatePresence>
                {isLoading ? (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan="6" className="py-10">
                      <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
                        {/* 🔄 Spinner */}
                        <div className="h-8 w-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                        <span className="text-sm">Loading templates...</span>
                        <span className="text-[11px] text-slate-400">
                          Please wait, fetching branch & category mappings.
                        </span>
                      </div>
                    </td>
                  </motion.tr>
                ) : isError ? (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td
                      colSpan="6"
                      className="text-center text-red-500 py-6"
                    >
                      Failed to load label templates
                      <div className="text-xs text-red-400 mt-1">
                        {error?.data?.message || error?.error || ""}
                      </div>
                    </td>
                  </motion.tr>
                ) : filteredTemplates?.length ? (
                  filteredTemplates.map((tpl, index) => {
                    const elements = Array.isArray(tpl.elements)
                      ? tpl.elements
                      : [];
                    const textCount = elements.filter(
                      (e) => e.type === "text"
                    ).length;
                    const barcodeCount = elements.filter(
                      (e) => e.type === "barcode"
                    ).length;
                    const qrCount = elements.filter(
                      (e) => e.type === "qrcode"
                    ).length;

                    return (
                      <motion.tr
                        key={tpl._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25, delay: index * 0.03 }}
                        className="border-t border-slate-100 hover:bg-[#f6f4ff] transition-colors"
                      >
                        {/* Name */}
                        <td className="px-4 py-2 align-top">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">
                                {tpl.name || "Untitled Template"}
                              </span>
                              {tpl.isDefault && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  <FaStar className="text-[9px]" />
                                  Default
                                </span>
                              )}
                            </div>
                            {tpl._id && (
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                ID:{" "}
                                <span className="font-mono break-all">
                                  {tpl._id}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Size */}
                        <td className="px-4 py-2 align-top">
                          <div className="text-sm font-medium text-slate-800">
                            {tpl.widthMm || 0} × {tpl.heightMm || 0}
                          </div>
                          {tpl.dpi && (
                            <div className="text-[10px] text-gray-400">
                              {tpl.dpi} dpi
                            </div>
                          )}
                        </td>

                        {/* Elements summary */}
                        <td className="px-4 py-2 align-top">
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                              <span className="font-semibold">
                                {textCount}
                              </span>
                              <span>Text</span>
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <FaBarcode className="text-[10px]" />
                              <span className="font-semibold">
                                {barcodeCount}
                              </span>
                              <span>Barcode</span>
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              <FaQrcode className="text-[10px]" />
                              <span className="font-semibold">{qrCount}</span>
                              <span>QR</span>
                            </span>
                          </div>
                        </td>

                        {/* Scope (Branch + Category) */}
                        <td className="px-4 py-2 align-top">
                          {renderScope(tpl)}
                        </td>

                        {/* Default toggle */}
                        <td className="px-4 py-2 text-center align-top">
                          {tpl.isDefault ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-[11px] cursor-default"
                              title="Current default template"
                            >
                              <FaStar className="text-[10px]" />
                              Default
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(tpl)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-300 text-[11px] hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200"
                              title="Set as default template"
                            >
                              <FaRegStar className="text-[10px]" />
                              Set Default
                            </button>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-2 align-top">
                          <div className="flex items-center gap-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="text-[#9481ce] text-sm p-2 rounded-full hover:bg-[#9481ce]/15"
                              onClick={() =>
                                navigate(`/designs/label/${tpl._id}/edit`)
                              }
                              title="Edit"
                            >
                              <FaPencilAlt />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="text-rose-500 text-sm p-2 rounded-full hover:bg-rose-50"
                              title="Delete"
                              onClick={() => openDeleteDialog(tpl)}
                            >
                              <FaTrashAlt />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                ) : (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan="6" className="text-center text-gray-500 py-8">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm">
                          No matching templates found.
                        </span>
                        {!templates?.length && (
                          <Link
                            to="/designs/label"
                            className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#9481ce] text-white text-xs shadow-sm hover:bg-[#7f6bce]"
                          >
                            + Create your first template
                          </Link>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      {/* 🧨 Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onCancel={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete label template?"
        message={
          deleteDialog.tpl
            ? `Template "${deleteDialog.tpl.name || "Untitled"}" will be permanently deleted.`
            : "This template will be permanently deleted."
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        destructive
      />
    </>
  );
};

export default LabelTemplateListPage;
